define("Pages/Event", [`Page`, `module`], function (Super, Self) {
	// shared between all instances

	let privateLinkEventId;

	return Super.inherit({
		namespace: Self.id,
		requires: [`User`, `Groups`, `Events`, `ShoppingCart`, `System`, `XHR`],
		constructor(setup) {
			const { App, Config, Events, ShoppingCart, System, Time, Utils, XHR, container, data } = this;
			const { trackingOptions } = data;
			const { eventId } = setup;
			const agentHandle = App.getCookie(`agentHandle`);
			const isAgentMode = this.isString(agentHandle, 1);
			const pltParam = App.getUrlParam(`plt`);
			const gstParam = App.getUrlParam(`gst`);
			const groupSaleUrlParam = App.getUrlParam(`gsurl`);
			const groupSaleId = App.getUrlParam(`gsId`);
			const tokenParam = App.getUrlParam(`stripe_redirect_token`);
			const params = {};
			const { isNativeApp } = System.get();
			const useWebBackButtonForApp = App.hasExternalCallback({ name: `onExitEvent` });

			let { referrer } = document;
			let referralId = App.getUrlParam(`r`);
			let accessCode = App.getUrlParam(`ac`);
			let privateLinkToken = null;
			let groupSaleToken = gstParam;
			let promise;
			let event;
			let group;

			if (isNativeApp && useWebBackButtonForApp) {
				this.addClass(`native-app-back-button`);
			}

			if (!this.isString(referralId, 1)) {
				referralId = null;
			}

			if (!this.isString(referrer, 1)) {
				referrer = null;
			}

			if (!this.isString(accessCode, 1)) {
				accessCode = null;
			}

			if (isAgentMode) {
				this.addClass(`is-agent`);
			}

			this.assign(data, { agentHandle, referralId, referrer });

			if (setup.eventMode === `PREVIEW`) {
				Events.setPreviewMode(true);
			}

			// check for a Private Link Token
			if (this.isString(pltParam, 1)) {
				if (privateLinkEventId === undefined || privateLinkEventId === setup.eventId) {
					privateLinkEventId = eventId;
					privateLinkToken = pltParam;

					params.plt = privateLinkToken;
				}
			}

			// check for a Group Sales Token
			if (this.isString(gstParam, 1)) {
				groupSaleToken = gstParam;
				params.gst = gstParam;
			}

			// check for an Agent handle
			if (isAgentMode) {
				params.agent = agentHandle;
			}

			promise = this.when({
				promises: [
					Events.get({
						by: `id`,
						force: true,
						id: eventId,
						params,
					}),
					ShoppingCart.init({
						accessCode,
						clear: false,
						eventId,
						privateLinkToken, // pass the plt into the cart init
						groupSaleId,
						groupSaleToken, // pass the gst into the cart init
						resumeCheckout: true, // resume checkout if one is already active
						groupSaleUrlParam,
					}),
				],
				spread: (_event) => {
					const { currencies } = _event;

					event = _event;
					group = event.group;

					data.event = event;

					ShoppingCart.get({
						subscribe: true,
						then: (cart) => {
							data.cart = cart;
						},
					});

					if (this.isString(tokenParam, 1)) {
						ShoppingCart.checkOrderComplete({ token: tokenParam });
					}

					// check for all in pricing modal
					if (event.feeInclusiveModal) {
						let message = `That means you'll see the cost of the ticket up-front including fees.`;

						if (event.orderLevelAdditionalFees > 0) {
							message = `That means you'll see the cost of the ticket up-front including fees. An additional order processing fee of ${Events.formatPrice(
								{
									price: event.orderLevelAdditionalFees,
									currency: currencies[0],
									type: `currencyCompact`,
								}
							)} may be added to each order.`;
						}

						if (event.hasSalesWithFlatShipping) {
							message += ` Any applicable shipping costs will be added at checkout.`;
						}

						//value, type, arg1, customClass, options = {}

						this.App.loadOverlay({
							type: `Message`,
							setup: {
								closable: false,
								heading: ` This event is using All-in Pricing.`,
								type: `confirmation`,
								message,
								yesLabel: `Accept & Continue`,
								noLabel: null,
								// mode: `ux4`,
							},
						});
					}

					const { logoThumbnailUrl } = group;

					trackingOptions.pageType = `Event`;
					trackingOptions.eventId = event.id;
					data.uid = `e.${event.id}`;

					if (this.isString(logoThumbnailUrl)) {
						return XHR.loadImage({
							src: logoThumbnailUrl,
							then: (img) => {
								data.logoSrc = logoThumbnailUrl;
								data.logoHref = `/groups/${group.subdomain}`;
								data.logoAlt = `Tixr - ${group.name}`;
							},
							fail: (reason) => {
								/** swallow the error */
							},
						});
					}
				},
			});

			promise = this.when({
				promise,
				then: () => {
					const currentTime = Time.getTime();
					const hashParams = App.getHashParams();
					const { hiddenDetailsList: eventHiddenDetailsList, id: eventId, galleryLayout, group, venue } = event;
					const { hiddenDetailsList: groupHiddenDetailsList } = group;
					const rightLinks = [];
					const leftLinks = [];
					const promises = [];
					let customOpacity;
					let title;

					title = `${event.name} | Tickets at Your Computer or Mobile Device`;
					if (this.isObject(venue) && venue.venueType !== `VIRTUAL`) {
						title += ` at ${venue.name} in ${venue.address.city}`;
					}
					title += ` | ${event.group.name} | Tixr`;

					this.assign(data, { title });

					container.setAttribute(`eventCategoryName`, event.eventCategoryName);
					container.setAttribute(`layout`, event.layout);

					data.event = event;
					data.isFollowing = false;
					data.saleStarted = currentTime > event.saleWindowStart;
					data.multiday = event.endDate - event.startDate > 86400000; // 1000 ms * 60 sec * 60 min * 24 hr

					event.headliners = [];
					event.artists = [];

					this.each(event.lineups, (lineup) => {
						const grouped = Utils.groupBy(lineup.acts, `type`);
						const headliners = this.map(grouped.HEADLINER || [], `artist`);
						const artists = this.map(grouped.SUPPORTING || [], `artist`);

						event.headliners = event.headliners.concat(headliners);
						event.artists = event.artists.concat(artists);
					});

					event.artistsCountOdd = event.artists.length % 2 === 1;

					if (this.isString(event.theme)) {
						// NOTE: this overrides the default handling in Controller
						data.theme = event.theme;
						this.container.setAttribute(`theme`, event.theme);
					}

					this.getCountDown();

					this.chain(event.sales)
						.filter((sale) => !(sale.state === `PRE` || sale.state === `HIDE`))
						.tap((activeSales) => {
							this.assign(data, {
								currentTime: Time.getTime(),
								salesActive: activeSales.length > 0,
							});
						});

					if (!data.salesActive) {
						this.addClass(`inactive`);
					}

					this.render();

					if (this.isFinite(event.backgroundContrastPercentage, 0)) {
						customOpacity = event.backgroundContrastPercentage * 0.01;
					} else if (event.backgroundContrast) {
						customOpacity = 0.8;
					} else {
						customOpacity = 0;
					}
					this.get(`[name="background-contrast"]`).style.opacity = customOpacity;

					this.select(`.legal > p`).addClass(`small`);

					leftLinks.push({
						type: `icon`,
						icon: `arrow-left`,
						href: `/groups/${group.subdomain}`,
						desktop: false,
						app: false,
					});

					if (this.isObject(event.rewardsPackage) && !this.includes(eventHiddenDetailsList, `REWARDS`)) {
						rightLinks.push({
							label: `Rewards`,
							handler: (evt) =>
								App.loadOverlay({
									type: `Amplify`,
									setup: { eventId, header: `Rewards`, amplifyEvent: true },
								}),
							theme: `PINK`,
							type: `button`,
							icon: `share-up`,
						});
					} else {
						rightLinks.push({
							label: `Share`,
							handler: (evt) =>
								App.loadOverlay({
									type: `Share`,
									setup: {
										eventId,
										type: `event`,
									},
								}),
							theme: `BLACK`,
							type: `button`,
							icon: `share-up`,
							mobile: false,
						});

						rightLinks.push({
							handler: (evt) =>
								App.loadOverlay({
									type: `Share`,
									setup: {
										eventId,
										type: `event`,
									},
								}),
							theme: `BLACK`,
							type: `icon-button`,
							icon: `share-up`,
							desktop: false,
						});
					}

					if (event.hacl === true) {
						rightLinks.push({
							icon: `lock-3`,
							handler: (evt) =>
								App.loadOverlay({
									setup: { eventId, scope: `event` },
									type: `EnterAccessCode`,
								}),
							type: `icon`,
							label: `Access Codes`,
						});
					}

					// if (!this.includes(eventHiddenDetailsList, `SHARE`)) {
					// 	share = {
					// 		type: `event`,
					// 		id: event.id,
					// 	};
					// }

					let showTixrLogo;
					if (this.isString(data.logoSrc, 1)) {
						const { logoAlt, logoHref, logoSrc } = data;

						showTixrLogo = false;
						leftLinks.unshift({
							type: `image`,
							alt: logoAlt,
							href: logoHref,
							src: logoSrc,
							// only shown on desktop
							desktop: true,
							app: false,
							mobile: false,
						});
					} else {
						showTixrLogo = true;
					}

					const mode = `ux3`;
					const showAvatar = !this.includes(eventHiddenDetailsList, `AVATAR`);
					const showSiteLinks = !this.includes(groupHiddenDetailsList, `FOOTER`);
					promises.push(
						this.create({
							namespace: `Modules/PageHeader`,
							setup: {
								name: `page-header`,
								showAvatar,
								showTixrLogo,
								rightLinks,
								leftLinks,
								showUserLinks: false,
							},
						}),
						this.create({
							namespace: `Modules/ParallaxBlur`,
							setup: {
								name: `parallax-blur-desktop`,
								backgroundImageUrl: event.backgroundUrl,
								scrollContainer: this.container,
							},
						}),
						this.create({
							namespace: `Modules/ParallaxBlur`,
							setup: {
								name: `parallax-blur-mobile`,
								backgroundImageUrl: event.mobileBackgroundImageUrl,
								scrollContainer: this.container,
							},
						}),
						this.create({
							namespace: `Modules/PageFooter`,
							setup: { mode, name: `page-footer`, showSiteLinks },
						}),
						this.create({
							namespace: `Modules/EventProducts`,
							setup: {
								eventId,
								name: `products`,
							},
						}),
						this.create({
							namespace: `Modules/Collapsible`,
							setup: {
								name: `description`,
								content: event.description,
							},
						}),
						this.create({
							namespace: `Modules/Collapsible`,
							setup: {
								name: `legal`,
								class: `small`,
								content: event.legal,
							},
						})
					);

					if (event.venue.venueType !== `VIRTUAL`) {
						if (
							event.eventCategoryName !== `Splash` &&
							event.pageLocationConfiguration === `MAP` &&
							!this.includes(eventHiddenDetailsList, `VENUE`)
						) {
							this.lazyCreate({
								namespace: `Modules/Map`,
								setup: {
									name: `map`,
									place: event.venue,
								},
							});
						}
					}

					if (galleryLayout === `GRID`) {
						this.createMediaForGrid();
					} else {
						this.createMediaForCarousel();
					}

					if (this.isObject(event.rewardsPackage)) {
						if (hashParams.rewards !== undefined) {
							App.loadOverlay({
								type: `Amplify`,
								setup: { eventId, header: `Rewards`, amplifyEvent: true },
							});
						} else if (hashParams.points !== undefined) {
							// check for #points in the URL
							App.loadOverlay({
								type: `PointsList`,
								setup: { eventId },
							});
						}
					}

					promises.push(
						this.create({
							namespace: `Modules/CheckoutFooter`,
							setup: {
								name: `checkout-footer`,
								eventId,
								displayCountdownClock: true,
								callbacks: {
									onToggleDisplay: (isDisplayed) => {
										const body = this.get(`.body`);

										if (isDisplayed) {
											this.removeClass(`checkout-footer-hide`, body);
										} else {
											this.addClass(`checkout-footer-hide`, body);
										}
									},
									zoom: ({ collectionId }) => {
										// zoom icon was clicked on a reserved sale
										App.loadOverlay({
											type: `ReservedCollection`,
											setup: { eventId, collectionId },
										});
									},
								},
							},
						})
					);

					if (this.isArray(event.headliners, 1)) {
						promises.push(
							this.create({
								namespace: `Widgets/ScrollContent2`,
								setup: {
									name: `headliner-scroll`,
									container: this.get(`[name="headliner-scroll"]`),
								},
							})
						);
					}

					if (this.isArray(event.artists, 1)) {
						promises.push(
							this.create({
								namespace: `Widgets/ScrollContent2`,
								setup: {
									name: `artist-scroll`,
									container: this.get(`[name="artist-scroll"]`),
								},
							})
						);
					}

					// handle when the event page is opened in an iframe by Box Office to clear the shopping cart on close
					window.addEventListener(
						`message`,
						(evt) => {
							if (evt.data === `closing`) {
								App.deleteCookie(`agentHandle`);
								ShoppingCart.init();
							}
						},
						false
					);

					const eventType = `product-list-view`;
					App.captureInsight({ eventId, eventType });

					return this.when({ promises });
				},
			});

			promise = this.when({
				promise,
				then: async () => {
					const { collectionConfiguration } = event;
					const queryParams = App.getQueryParams();
					let hasAccessCodeParam = false;
					let hasFilterParams = false;
					let queryAction;
					let queryIds;

					this.each(queryParams, (value, key) => {
						if (!this.isString(value)) {
							return;
						}

						value = value.toUpperCase();
						key = key.toUpperCase();

						switch (key) {
							case `COL`:
								queryIds = value.split(`,`);
								queryIds = this.map(queryIds, (id) => this.parseInt(id));
								break;

							case `A`:
								queryAction = value;
								break;

							case `AC`:
								if (this.isString(value, 1)) {
									hasAccessCodeParam = true;
								}
								break;
							case `FILTER`:
								if (this.isString(value, 1)) {
									hasFilterParams = true;
								}
								break;
						}
					});

					if (!this.isString(data.cart.token)) {
						// make sure checkout isn't already in progress
						if (queryAction === `L`) {
							if (this.isString(groupSaleToken, 1)) {
								// Group Sales
								await App.loadOverlay({
									// await so the event page isn't displayed in the background before loading the overlay
									type: `ReservedCollection`,
									setup: {
										callbacks: {
											die: () => {
												if (data.cart.quantity === 0) {
													ShoppingCart.navigateToOriginalGroupSale();
												}
											},
										},
										eventId,
									},
								});
							} else if (this.isArray(queryIds, 1) && this.isObject(collectionConfiguration)) {
								const { collections } = collectionConfiguration;
								const [id] = queryIds;
								const collection = this.find(collections, { id });

								if (this.isObject(collection)) {
									// URL "launch" parameter is pointing to a valid collection
									const { layout } = collection;
									// launch the appropriate overlay based on whether reserved sales are present
									const type = this.includes(Config.RESERVED_COLLECTION_LAYOUTS, layout)
										? `ReservedCollection`
										: `Collection`;

									App.loadOverlay({
										type,
										setup: {
											callbacks: {
												die: () => {
													// clear any query params that may have been used to launch this
													App.setQueryParam(`A`, null);
													App.setQueryParam(`COL`, null);
													App.setQueryParam(`SID`, null);
												},
											},
											collectionId: id,
											eventId,
										},
										then: (collectionOverlay) => {
											if (event.hacl && !hasAccessCodeParam && hasFilterParams) {
												const collectionHacl = collectionOverlay.getTicketPicker().getHasAccessCodeLock();
												if (collectionHacl) {
													App.loadOverlay({
														setup: { eventId, scope: `event` },
														type: `EnterAccessCode`,
													});
												}
											}
										},
									});
								}
							}
						} else if (queryAction === `R`) {
							App.loadOverlay({
								type: `Amplify`,
								setup: { eventId, header: `Rewards`, amplifyEvent: true },
							});
							App.setQueryParam(`R`, null);
						}
					}
				},
			});

			return promise;
		},

		ready(setup) {
			const { App, DOM, System } = this;
			const contentFooterDimensions = DOM.getDimensions(this.get(`.content.footer`));
			const buttonWrapper = this.get(`[name="checkout-footer"]`);
			const { style: buttonStyles } = buttonWrapper;

			let gap = 0;
			App.onScroll(({ scrollTop }) => {
				const system = System.get();
				const { breakpoint } = system;
				const isMobile = breakpoint === `mobile`;
				let newGap = 0;

				if (!isMobile) {
					const { viewportBottom } = this.dimensions;
					const { height: footerHeight } = contentFooterDimensions;
					const difference = footerHeight + viewportBottom;

					if (difference > 0) {
						newGap = difference;
					}
				}

				if (newGap !== gap) {
					if (gap < 0) {
						gap = 0;
					}

					buttonStyles.transform = `translate3d(0,-${newGap}px, 0)`;
					gap = newGap;
				}
			});
		},

		api: {
			createMediaForCarousel: {
				value() {
					const { event } = this.data;
					const { videoId } = event;
					let allMedia = this.clone(event.media);
					let video;

					if (this.isString(videoId, 1)) {
						video = {
							id: -1,
							ordinal: -1,
							target: `GALLERY`,
							type: `VIDEO`,
							videoId,
						};

						if (this.isArray(allMedia, 1)) {
							allMedia.unshift(video);
						} else {
							allMedia = [video];
						}
					}

					if (this.isArray(allMedia, 1)) {
						this.chain(allMedia)
							.sortBy(`ordinal`)
							.tap(function (media) {
								allMedia = media;
							});

						const alignWithElement = this.get(`.gallery > h2`);

						this.lazyCreate({
							namespace: `Modules/ImageGallery`,
							setup: {
								name: `image-gallery`,
								media: allMedia,
								layout: `horizontal`,
								mode: `highlight`,
								alignWithElement,
							},
						});
					} else {
						this.hide({ target: this.get(`.gallery`) });
					}
				},
			},

			createMediaForGrid: {
				value() {
					const { event } = this.data;
					const { videoId } = event;
					const videoElement = this.get(`.content.video`);

					if (this.isArray(event.media, 1)) {
						this.chain(event.media)
							.sortBy(`ordinal`)
							.tap(function (media) {
								event.media = media;
							});

						this.lazyCreate({
							namespace: `Modules/ImageGallery`,
							setup: {
								name: `image-gallery`,
								media: event.media,
							},
						});
					} else {
						this.hide({ target: this.get(`.gallery`) });
					}

					if (this.isString(videoId, 1)) {
						this.getVideoThumbnail({
							videoId,
							then: (imageElement) => {
								// thumbnail loaded successfully
								const { src } = imageElement;
								const thumbnailElement = this.get(`.background.thumbnail`, videoElement);

								thumbnailElement.style.backgroundImage = `url("${src}")`;
							},
							fail: (reason) => {
								// error loading thumbnail
								const type = `warning`;
								const message = `Error loading YouTube thumbnail for video ${videoId}`;

								this.log({ message, type });

								// create the embedded player
								this.createVideoPlayer({ autoplay: false });
							},
							fin: () =>
								this.show({
									target: videoElement,
									transition: `height`,
								}),
						});
					}
				},
			},

			getVideoThumbnail: {
				async: true,
				value({ videoId }) {
					const MIN_YOUTUBE_THUMBNAIL_HEIGHT = 200;
					const { XHR } = this;
					const srcPrefix = `//img.youtube.com/vi`;
					const srcSuffixes = [`maxresdefault.jpg`, `hqdefault.jpg`];
					let checkImage;
					let promise;
					let src;

					checkImage = (element) => {
						if (this.isElement(element)) {
							// assume the 404 response if image is very small
							if (this.isFinite(element.height, MIN_YOUTUBE_THUMBNAIL_HEIGHT)) {
								return true;
							}
						}

						return false;
					};

					this.each(srcSuffixes, (srcSuffix) => {
						const onError = () => {
							src = `${srcPrefix}/${videoId}/${srcSuffix}`;
							return XHR.loadImage({ src });
						};

						promise = this.when({
							promise,
							then: (image) => {
								if (checkImage(image)) {
									return image;
								} else {
									return onError();
								}
							},
							fail: onError,
						});
					});

					return this.when({
						promise,
						then: (image) => {
							if (checkImage(image)) {
								return image;
							} else {
								throw new Error(`Couldn't load YouTube thumbnail for id "${videoId}"`);
							}
						},
					});
				},
			},

			createVideoPlayer: {
				defaults: {
					autoplay: true,
				},
				value({ autoplay }) {
					const { data } = this;
					const { event } = data;
					const { videoId } = event;
					const name = `video-player`;

					this.create({
						namespace: `Modules/VideoPlayer`,
						setup: { autoplay, name, videoId },
						then: () => {
							const target = this.get(`.content.video`);
							this.addClass(`playing`, target);
						},
					});
				},
			},

			getCountDown: {
				value() {
					const { data, Time } = this;
					const { event } = data;
					const { sales, saleWindowStart, venue } = event;
					const inactiveStates = [`HIDE`, `PRE`];
					// find a sale that should be shown
					const activeSale = this.find(sales, ({ state }) => !this.includes(inactiveStates, state));
					const hasActiveSale = this.isObject(activeSale);
					const now = Time.getTime();
					const isSaleStarted = now > saleWindowStart;

					// Carousel cards have this information in their own badge
					if (hasActiveSale || isSaleStarted) {
						this.getCountDownToSaleEnd();
					} else {
						data.countdown = `On sale ${Time.format(`%W %m %d`, saleWindowStart, venue.timezone)} at ${Time.format(
							`%H:%i%a %t`,
							saleWindowStart,
							venue.timezone
						)}`;
					}
				},
			},

			getCountDownToSaleEnd: {
				value() {
					const { data, Time } = this;
					const { event } = data;
					const { venue } = event;
					const currentTime = Time.getTime();
					const timeLeft = event.startDate - currentTime;
					const SECOND = 1000;
					const MINUTE = SECOND * 60;
					const HOUR = MINUTE * 60;
					const DAY = HOUR * 24;
					let count;
					let label;

					if (!this.includes(event.hiddenDetailsList, `DATE`)) {
						if (timeLeft > DAY) {
							count = Time.daysUntil(event.startDate, venue.timezone);
							const isPlural = this.isInt(count) && count !== 1;
							label = isPlural ? `days` : `day`;
						} else if (!this.includes(event.hiddenDetailsList, `TIME`)) {
							if (timeLeft > HOUR) {
								count = Math.floor(timeLeft / HOUR);
								const isPlural = this.isInt(count) && count !== 1;
								label = isPlural ? `hours` : `hour`;
							} else if (timeLeft > MINUTE) {
								count = Math.floor(timeLeft / MINUTE);
								const isPlural = this.isInt(count) && count !== 1;
								label = isPlural ? `minutes` : `minute`;
							} else if (timeLeft > SECOND) {
								count = Math.floor(timeLeft / SECOND);
								const isPlural = this.isInt(count) && count !== 1;
								label = isPlural ? `seconds` : `second`;
							}
						}
					}

					if (count !== undefined) {
						data.countdown = `${count} ${label} to event`;
					}
				},
			},
		},

		destructor(options) {
			const { App } = this;

			// clear any event page query params
			App.setQueryParam(`A`, null);
			App.setQueryParam(`COL`, null);
			App.setQueryParam(`SID`, null);
		},

		listeners: [
			{
				event: `click`,
				filter: `[action]`,
				handler(evt) {
					const { App, DOM, ShoppingCart } = this;
					const { currentTarget } = evt;
					const action = currentTarget.getAttribute(`action`);

					switch (action) {
						case `close-blurb`:
							return this.hide({
								selector: `.blurb`,
								transition: `height-smooth`,
								then: function () {
									DOM.trigger({
										event: `resize`,
										target: window,
									});
								},
							});

						case `not-agent`:
							App.deleteCookie(`agentHandle`);
							// ensure shopping cart is cleared
							return ShoppingCart.init({
								then: () => window.location.reload(),
							});

						case `play-video`:
							return this.createVideoPlayer({ autoplay: true });

						case `backToApp`:
							return App.callExternal({
								name: `onExitEvent`,
							});
					}
				},
			},
		],
	});
});

define("Pages/Event.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Pages/Event/main"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"agent\"> <p>Agent: <a href=\"/agents/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"agentHandle") : depth0), depth0))
    + "\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"agentHandle") : depth0), depth0))
    + "</a> <span class=\"small\">( <a href=\"#\" action=\"not-agent\">Not you?</a> )</span></p> </div> ";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"shortBlurb") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":214},"end":{"line":1,"column":378}}})) != null ? stack1 : "")
    + "  <div class=\"head\"> <div name=\"page-header\" class=\"hide-app hide-agent "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"showLogo")),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":488},"end":{"line":1,"column":526}}})) != null ? stack1 : "")
    + "\"></div> </div> <a href=\"#\" action=\"backToApp\" aria-label=\"Back\" class=\"button ux4 icon tertiary\" theme=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"theme") : depth0), depth0))
    + "\"> "
    + alias3(lookupProperty(helpers,"getIcon").call(alias1,"arrow-left",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":643},"end":{"line":1,"column":667}}}))
    + " </a> <div name=\"parallax-blur-desktop\" class=\"hide-mobile\"></div> <div name=\"parallax-blur-mobile\" class=\"hide-desktop\"></div> <div name=\"background-contrast\" class=\"hide-mobile\"></div> <div name=\"background-gradient\"></div> <div class=\"body checkout-footer-hide\"> <div class=\"content\" name=\"products\"></div> <div class=\"content centered description\"> <h2 class=\"transition-content\">Description</h2> <div class=\"badges transition-content\"> "
    + ((stack1 = lookupProperty(helpers,"missing").call(alias1,(depth0 != null ? lookupProperty(depth0,"hiddenDetailsList") : depth0),"AGE",{"name":"missing","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1108},"end":{"line":1,"column":1247}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"doorsOpenLabelLocale") : depth0),{"name":"if","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1248},"end":{"line":1,"column":1393}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"countdown")),{"name":"if","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1394},"end":{"line":1,"column":1502}}})) != null ? stack1 : "")
    + " </div> <div name=\"description\" class=\"transition-content\"></div> </div> "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"galleryLayout") : depth0),"GRID",{"name":"compare","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1575},"end":{"line":1,"column":1875}}})) != null ? stack1 : "")
    + " <div class=\"content centered gallery hide-agent "
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"galleryLayout") : depth0), depth0))
    + "\"> <h2 class=\"transition-content\">Gallery</h2> <div name=\"image-gallery\" class=\"transition-content\"></div> </div> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"headliners") : depth0),{"name":"if","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":2055},"end":{"line":1,"column":2487}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"artists") : depth0),{"name":"if","hash":{},"fn":container.program(24, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":2488},"end":{"line":1,"column":2898}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"missing").call(alias1,(depth0 != null ? lookupProperty(depth0,"hiddenDetailsList") : depth0),"VENUE",{"name":"missing","hash":{},"fn":container.program(31, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":2899},"end":{"line":1,"column":3516}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"eventCategoryName") : depth0),"Splash","!=",{"name":"compare","hash":{},"fn":container.program(41, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":3517},"end":{"line":1,"column":4192}}})) != null ? stack1 : "")
    + " <div class=\"content centered legal hide-agent\"> <h2 class=\"transition-content\">Terms</h2> <div name=\"legal\" class=\"transition-content\"></div> </div> </div> <div name=\"checkout-footer\"></div> <div class=\"content footer\"> <div name=\"page-footer\"></div> </div> ";
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"blurb\"> <p>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"shortBlurb") : depth0), depth0)) != null ? stack1 : "")
    + "</p> <a href=\"#\" action=\"close-blurb\" aria-label=\"hide banner\">"
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"closesmall",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":335},"end":{"line":1,"column":359}}}))
    + "</a> </div> ";
},"6":function(container,depth0,helpers,partials,data) {
    return " has-logo";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <span class=\"badge age-restriction\">"
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"customer-ux3",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":1181},"end":{"line":1,"column":1207}}}))
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"ageRestriction") : depth0), depth0)) != null ? stack1 : "")
    + "</span> ";
},"10":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <span class=\"badge doors-open-label whitespace-pre-wrap\">"
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"time",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":1334},"end":{"line":1,"column":1352}}}))
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"doorsOpenLabelLocale") : depth0), depth0)) != null ? stack1 : "")
    + "</span> ";
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <span class=\"badge countdown\">"
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"time",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":1448},"end":{"line":1,"column":1466}}}))
    + ((stack1 = container.lambda(((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"countdown")), depth0)) != null ? stack1 : "")
    + "</span> ";
},"14":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"videoId") : depth0),{"name":"if","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1609},"end":{"line":1,"column":1862}}})) != null ? stack1 : "")
    + " ";
},"15":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"content video hide-agent\"> <div class=\"background thumbnail transition-content\"> <a href=\"#\" action=\"play-video\" class=\"transition-content\"> "
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"play-circle",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":1778},"end":{"line":1,"column":1803}}}))
    + " </a> <div name=\"video-player\"></div> </div> </div> ";
},"17":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"content centered artists\"> <h2 class=\"transition-content\">"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"headlinerLabel") : depth0),{"name":"if","hash":{},"fn":container.program(18, data, 0),"inverse":container.program(20, data, 0),"data":data,"loc":{"start":{"line":1,"column":2144},"end":{"line":1,"column":2209}}})) != null ? stack1 : "")
    + "</h2> <div name=\"headliner-scroll\" class=\"transition-content\"> <ul class=\"list\"> "
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"headliners") : depth0),{"name":"each","hash":{},"fn":container.program(22, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":2290},"end":{"line":1,"column":2459}}})) != null ? stack1 : "")
    + " </ul> </div> </div> ";
},"18":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"headlinerLabel") : depth0), depth0));
},"20":function(container,depth0,helpers,partials,data) {
    return "Headliners";
},"22":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <li class=\"headliner\"> <div class=\"background\" style=\"background-image:url('"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"imageUrl") : depth0), depth0))
    + "');\"></div> <h3 translate=\"no\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "</h3> </li> ";
},"24":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"content centered artists\"> <h2 class=\"transition-content\">"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"artistLabel") : depth0),{"name":"if","hash":{},"fn":container.program(25, data, 0),"inverse":container.program(27, data, 0),"data":data,"loc":{"start":{"line":1,"column":2574},"end":{"line":1,"column":2629}}})) != null ? stack1 : "")
    + "</h2> <div name=\"artist-scroll\" class=\"transition-content\"> <ul class=\"list\"> "
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"artists") : depth0),{"name":"each","hash":{},"fn":container.program(29, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":2707},"end":{"line":1,"column":2870}}})) != null ? stack1 : "")
    + " </ul> </div> </div> ";
},"25":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"artistLabel") : depth0), depth0));
},"27":function(container,depth0,helpers,partials,data) {
    return "Talent";
},"29":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <li class=\"artist\"> <div class=\"background\" style=\"background-image:url('"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"imageUrl") : depth0), depth0))
    + "');\"></div> <h3 translate=\"no\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "</h3> </li> ";
},"31":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"eventCategoryName") : depth0),"Splash","!=",{"name":"compare","hash":{},"fn":container.program(32, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":2938},"end":{"line":1,"column":3503}}})) != null ? stack1 : "")
    + " ";
},"32":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"venue") : depth0)) != null ? lookupProperty(stack1,"venueType") : stack1),"VIRTUAL","!=",{"name":"compare","hash":{},"fn":container.program(33, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":3014},"end":{"line":1,"column":3490}}})) != null ? stack1 : "")
    + " ";
},"33":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"pageLocationConfiguration") : depth0),"HIDE","!=",{"name":"compare","hash":{},"fn":container.program(34, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":3058},"end":{"line":1,"column":3477}}})) != null ? stack1 : "")
    + " ";
},"34":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"content centered location\"> <h2 class=\"transition-content\">Location</h2> "
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"pageLocationConfiguration") : depth0),"MAP",{"name":"compare","hash":{},"fn":container.program(35, data, 0),"inverse":container.program(37, data, 0),"data":data,"loc":{"start":{"line":1,"column":3194},"end":{"line":1,"column":3457}}})) != null ? stack1 : "")
    + " </div> ";
},"35":function(container,depth0,helpers,partials,data) {
    return " <div name=\"map\" class=\"transition-content\"></div> ";
},"37":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"pageLocationConfiguration") : depth0),"BLURB",{"name":"compare","hash":{},"fn":container.program(38, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":3289},"end":{"line":1,"column":3445}}})) != null ? stack1 : "");
},"38":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"locationBlurb") : depth0),{"name":"if","hash":{},"fn":container.program(39, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":3340},"end":{"line":1,"column":3444}}})) != null ? stack1 : "")
    + " ";
},"39":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"map-blurb transition-content\"> <p>"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"locationBlurb") : depth0), depth0))
    + "</p> </div> ";
},"41":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"venue") : depth0),{"name":"with","hash":{},"fn":container.program(42, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":3593},"end":{"line":1,"column":4153}}})) != null ? stack1 : "")
    + " ";
},"42":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"content centered contact hide-agent\"> <h2 class=\"transition-content\">Contact</h2> <div class=\"table transition-content\"> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"phone") : depth0),{"name":"if","hash":{},"fn":container.program(43, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":3742},"end":{"line":1,"column":3834}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"email") : depth0),{"name":"if","hash":{},"fn":container.program(45, data, 0),"inverse":container.program(47, data, 0),"data":data,"loc":{"start":{"line":1,"column":3835},"end":{"line":1,"column":4033}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"website") : depth0),{"name":"if","hash":{},"fn":container.program(49, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":4034},"end":{"line":1,"column":4129}}})) != null ? stack1 : "")
    + " </div> </div> ";
},"43":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div> <p>Phone</p> <p><a href=\"tel:"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"phone") : depth0), depth0))
    + "\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"phone") : depth0), depth0))
    + "</a></p> </div> ";
},"45":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div> <p>Email</p> <p><a href=\"mailto:"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"email") : depth0), depth0))
    + "\" translate=\"no\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"email") : depth0), depth0))
    + "</a></p> </div> ";
},"47":function(container,depth0,helpers,partials,data) {
    return " <div> <p>Support</p> <p><a href=\"//support.tixr.com\">Click Here</a></p> </div> ";
},"49":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div> <p>Website</p> <p><a href=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"website") : depth0), depth0))
    + "\">Click Here</a></p> </div> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"agentHandle") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":197}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"with").call(alias1,(depth0 != null ? lookupProperty(depth0,"event") : depth0),{"name":"with","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":198},"end":{"line":1,"column":4460}}})) != null ? stack1 : "");
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=Event.js.map